import { z } from 'zod';

export const PAPERCUT_BROWSER_CONSOLE_LEVELS = [
  'error',
  'warn',
  'log',
  'info',
  'debug',
] as const;

export type PapercutBrowserConsoleLevel = (typeof PAPERCUT_BROWSER_CONSOLE_LEVELS)[number];

export interface PapercutBrowserConsoleLogEntry {
  timestamp: string;
  level: PapercutBrowserConsoleLevel;
  message: string;
  stack?: string;
}

export const papercutBrowserConsoleLogEntrySchema = z.object({
  timestamp: z.string(),
  level: z.enum(PAPERCUT_BROWSER_CONSOLE_LEVELS),
  message: z.string().max(10_000),
  stack: z.string().max(50_000).optional(),
});

export const papercutBrowserConsoleLogsSchema = z
  .array(papercutBrowserConsoleLogEntrySchema)
  .max(200);

export function normalizePapercutBrowserConsoleLogs(
  value: unknown,
): PapercutBrowserConsoleLogEntry[] {
  const parsed = papercutBrowserConsoleLogsSchema.safeParse(value);
  return parsed.success ? (parsed.data as PapercutBrowserConsoleLogEntry[]) : [];
}

function inferConsoleLevelFromPaste(text: string): PapercutBrowserConsoleLevel {
  const lower = text.toLowerCase();
  if (lower.includes('uncaught') || lower.includes('error:') || /\berror\b/.test(lower)) {
    return 'error';
  }
  if (lower.includes('warning') || /\bwarn\b/.test(lower)) {
    return 'warn';
  }
  if (/\binfo\b/.test(lower)) {
    return 'info';
  }
  if (/\bdebug\b/.test(lower)) {
    return 'debug';
  }
  return 'log';
}

/** Merge auto-captured logs with optional text pasted from DevTools. */
export function mergePapercutConsoleLogs(
  captured: PapercutBrowserConsoleLogEntry[],
  manualPasteText: string,
): PapercutBrowserConsoleLogEntry[] {
  const trimmed = manualPasteText.trim();
  if (!trimmed) {
    return captured.slice(0, 200);
  }

  const manualEntry: PapercutBrowserConsoleLogEntry = {
    timestamp: new Date().toISOString(),
    level: inferConsoleLevelFromPaste(trimmed),
    message:
      trimmed.length > 10_000
        ? `${trimmed.slice(0, 10_000)}…`
        : trimmed,
  };

  return [...captured, manualEntry].slice(0, 200);
}
